import React from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, X, Truck } from "lucide-react";
import { buildCartEventPayload, META_PIXEL_EVENTS, trackMetaEvent } from "../../utils/metaPixel";

const formatDrawerPrice = (price) => `Rs.${price}/-`;
const FREE_SHIPPING_THRESHOLD = 599;

function CartDrawer({
  isOpen,
  items,
  onClose,
  onIncrement,
  onDecrement,
  onRemove,
  totalAmount,
  checkoutPath = "/checkout",
}) {
  const navigate = useNavigate();

  const currentTotal =
    Number(totalAmount) ||
    items.reduce(
      (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1),
      0
    );

  const isFreeShippingUnlocked = currentTotal >= FREE_SHIPPING_THRESHOLD;
  const amountAway = Math.max(0, FREE_SHIPPING_THRESHOLD - currentTotal);
  const progressPercent = Math.min(
    100,
    Math.max(0, (currentTotal / FREE_SHIPPING_THRESHOLD) * 100)
  );

  const handleCheckoutClick = () => {
    trackMetaEvent(
      META_PIXEL_EVENTS.INITIATE_CHECKOUT,
      buildCartEventPayload(items)
    );
    onClose();
    navigate(checkoutPath);
  };

  const drawer = (
    <>
      <div
        className={`cart-drawer-backdrop${isOpen ? " cart-drawer-backdrop--open" : ""}`}
        onClick={onClose}
        aria-hidden={!isOpen}
      />

      <aside
        className={`cart-drawer${isOpen ? " cart-drawer--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-drawer-title"
        aria-hidden={!isOpen}
      >
        <div className="cart-drawer__panel">
          <div className="cart-drawer__header">
            <h2 id="cart-drawer-title">Your Cart</h2>
            <button
              type="button"
              className="cart-drawer__close"
              onClick={onClose}
              aria-label="Close cart drawer"
            >
              <X size={28} strokeWidth={1.75} />
            </button>
          </div>

          {/* Simple Free Shipping Progress Bar matching website theme */}
          {items.length > 0 && (
            <div className="cart-drawer__shipping-bar">
              <div className="cart-drawer__shipping-text">
                {isFreeShippingUnlocked ? (
                  <span className="cart-drawer__shipping-unlocked">
                    🎉 Congratulations! You unlocked <strong>FREE SHIPPING!</strong>
                  </span>
                ) : (
                  <span className="cart-drawer__shipping-away">
                    Add <strong>₹{amountAway.toFixed(2)}</strong> more to get <strong>FREE SHIPPING!</strong>
                  </span>
                )}
              </div>

              {/* Progress bar with moving circle, message box, and max amount marker */}
              <div className="cart-drawer__shipping-track-wrap">
                <div className="cart-drawer__shipping-track">
                  <div
                    className={`cart-drawer__shipping-fill ${isFreeShippingUnlocked ? "cart-drawer__shipping-fill--unlocked" : ""
                      }`}
                    style={{ width: `${progressPercent}%` }}
                  />

                  {/* Truck icon on the progress bar with message-like box of cart value */}
                  <div
                    className="cart-drawer__shipping-thumb-wrap"
                    style={{ left: `${progressPercent}%` }}
                  >
                    <div
                      className={`cart-drawer__shipping-bubble ${isFreeShippingUnlocked ? "cart-drawer__shipping-bubble--unlocked" : ""
                        }`}
                    >
                      ₹{currentTotal.toFixed(2)}
                      <span className="cart-drawer__shipping-bubble-arrow" />
                    </div>
                    <div
                      className={`cart-drawer__shipping-truck ${isFreeShippingUnlocked ? "cart-drawer__shipping-truck--unlocked" : ""
                        }`}
                    >
                      <Truck size={18} strokeWidth={2.2} />
                    </div>
                  </div>

                  {/* End marker at max amount (599) */}
                  <div
                    className={`cart-drawer__shipping-end-circle ${isFreeShippingUnlocked ? "cart-drawer__shipping-end-circle--unlocked" : ""
                      }`}
                  />
                </div>

                {/* Scale labels below progress bar */}
                <div className="cart-drawer__shipping-scale">
                  <span className="cart-drawer__scale-start">₹0</span>
                  <span
                    className={`cart-drawer__scale-end ${isFreeShippingUnlocked ? "cart-drawer__scale-end--unlocked" : ""
                      }`}
                  >
                    ₹599
                  </span>
                </div>
              </div>
            </div>
          )}

          {items.length > 0 ? (
            <>
              <div className="cart-drawer__body">
                <div className="cart-drawer__items">
                  {items.map((item) => {
                    const showOriginalPrice =
                      Number(item.originalPrice) > Number(item.price);

                    return (
                      <article className="cart-drawer__item" key={item.id}>
                        <div className="cart-drawer__thumb">
                          <img src={item.image} alt={item.name} />
                        </div>

                        <div className="cart-drawer__item-content">
                          <div className="cart-drawer__item-copy">
                            <h3>{item.name}</h3>
                            <p>{item.packSize}</p>
                          </div>

                          <div className="cart-drawer__item-meta">
                            <div className="cart-drawer__price-group">
                              <span className="cart-drawer__item-price">
                                {formatDrawerPrice(item.price)}
                              </span>
                              {showOriginalPrice && (
                                <span className="cart-drawer__item-price-old">
                                  {formatDrawerPrice(item.originalPrice)}
                                </span>
                              )}

                            </div>

                            <button
                              type="button"
                              className="cart-drawer__remove"
                              onClick={() => onRemove(item.id)}
                            >
                              <Trash2 size={17} />
                              Remove
                            </button>
                          </div>

                          <div className="cart-drawer__qty">
                            <button
                              type="button"
                              className="cart-drawer__qty-btn"
                              onClick={() => onDecrement(item.id)}
                              aria-label={`Decrease quantity for ${item.name}`}
                            >
                              <Minus size={19} />
                            </button>

                            <span className="cart-drawer__qty-value">
                              {item.quantity}
                            </span>

                            <button
                              type="button"
                              className="cart-drawer__qty-btn"
                              onClick={() => onIncrement(item.id)}
                              aria-label={`Increase quantity for ${item.name}`}
                            >
                              <Plus size={19} />
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>

              <div className="cart-drawer__footer">
                <div className="cart-drawer__total">
                  <span>Total Amount</span>
                  <strong>{formatDrawerPrice(totalAmount)}</strong>
                </div>

                <button
                  type="button"
                  className="cart-drawer__checkout"
                  onClick={handleCheckoutClick}
                >
                  CHECKOUT NOW
                </button>

                <div
                  className="cart-drawer__payments"
                  aria-label="Accepted payment methods"
                >
                  <img
                    src="/kuku-assets/images/payment-badges.png"
                    alt="Accepted payment methods"
                  />
                </div>

                <div className="cart-drawer__links">
                  <Link to="/terms-conditions" onClick={onClose}>
                    Terms &amp; Conditions
                  </Link>
                  <span>-</span>
                  <Link to="/return-refund-policy" onClick={onClose}>
                    Return &amp; Refund Policy
                  </Link>
                </div>
              </div>
            </>
          ) : (
            <div className="cart-drawer__empty">
              <p>Your cart is currently empty.</p>
              <Link to="/categories" onClick={onClose}>
                Continue Shopping
              </Link>
            </div>
          )}
        </div>
      </aside>
    </>
  );

  if (typeof document === "undefined") {
    return drawer;
  }

  return createPortal(drawer, document.body);
}

export default CartDrawer;
